import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { FullPageSpinner } from "@/components/ui/full-page-spinner";
import { PageColumn, PageShell } from "@/components/ui/page-shell";
import { useAuthState } from "@/lib/auth-state";
import { signInSearch } from "@/lib/sign-in-redirect";

export const Route = createFileRoute("/account/settings")({
	component: SettingsPage,
});

function SettingsPage() {
	const { session, isReady } = useAuthState();

	if (!isReady) return <FullPageSpinner />;

	if (!session?.user) {
		return (
			<div className="mx-auto w-full max-w-md space-y-4 px-4 py-8 text-center">
				<h1 className="text-2xl font-semibold">Account settings</h1>
				<p className="text-muted-foreground">
					Sign in to view your profile and account details.
				</p>
				<Button asChild>
					<Link
						to="/auth/login"
						search={signInSearch({
							intent: "sign-in",
							callbackUrl: "/account/settings",
						})}
					>
						Sign in
					</Link>
				</Button>
			</div>
		);
	}

	const { user } = session;
	const name = user.name?.trim() || "User";

	return (
		<PageShell className="py-8">
			<PageColumn className="space-y-8">
				<div className="space-y-1">
					<h1 className="text-3xl font-semibold leading-tight">
						Account settings
					</h1>
					<p className="text-sm text-muted-foreground">
						Your personal profile and sign-in details.
					</p>
				</div>

				<section
					aria-labelledby="account-profile-heading"
					className="space-y-5 border-b border-border pb-8"
				>
					<h2 id="account-profile-heading" className="text-sm font-medium">
						Profile
					</h2>
					<div className="flex min-w-0 items-center gap-4">
						{user.image ? (
							<img
								src={user.image}
								alt=""
								referrerPolicy="no-referrer"
								className="h-12 w-12 shrink-0 rounded-full object-cover"
							/>
						) : (
							<div
								aria-hidden="true"
								className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted text-lg font-medium"
							>
								{(user.name?.trim() || user.email || "U")
									.charAt(0)
									.toUpperCase()}
							</div>
						)}
						<div className="min-w-0 space-y-1">
							<p className="break-words font-medium">{name}</p>
							<p className="break-all text-sm text-muted-foreground">
								{user.email}
							</p>
						</div>
					</div>
					<details className="space-y-3">
						<summary className="w-fit cursor-pointer rounded-sm text-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
							Account details
						</summary>
						<dl className="space-y-1 text-sm">
							<dt className="text-muted-foreground">User ID</dt>
							<dd className="break-all font-mono text-xs">{user.id}</dd>
						</dl>
					</details>
				</section>

				<section
					aria-labelledby="account-session-heading"
					className="space-y-4"
				>
					<div className="space-y-1">
						<h2 id="account-session-heading" className="text-sm font-medium">
							Session
						</h2>
						<p className="text-sm text-muted-foreground">
							Sign out of your account on this device.
						</p>
					</div>
					<Button variant="outline" asChild>
						<Link to="/auth/logout">Sign out</Link>
					</Button>
				</section>
			</PageColumn>
		</PageShell>
	);
}
